import React from 'react'
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import setupApp from '../helpers/setupCheckoutApp';
import App from '../listing/App';
import TicketsButtonContainer from "./containers/TicketsButtonContainer";
import ListingDatePicker from "./views/series/containers/ListingDatePicker";
import { setUseFocusTrap, setIsEmbed, setEmbedType, setAlwaysShowHiddenLevels, setCheckoutOverlay } from './actions/ui';
import { setOrgListingConfig, updateOrgListingConfig } from './actions/org-listing-config';
import EmbeddedComponentWrapper from './components/EmbeddedComponentWrapper';
import EmbeddedTicketPicker from './views/ticket-picker/containers/EmbeddedTicketPicker';
import IframeResizer from 'iframe-resizer';
import { setEmbedToken } from './helpers/embedToken';
import {
    shouldPinEmbeddedCart,
    showContinueCheckoutBanner,
    getEmbedCartBehavior,
} from './selectors/ui';
import {
    EMBED_TYPE_BUTTON,
    EMBED_TYPE_TICKET_LIST,
    EMBED_TYPE_CALENDAR,
    EMBED_TYPE_MULTI,
    EMBED_CART_BEHAVIOR_PIN_TO_TOP,
    EMBED_CART_BEHAVIOR_PIN_TO_BOTTOM,
    EMBED_TYPE_NO_COMPONENT,
} from './reducers/ui';
import MarketingHelper from './helpers/marketing/marketingHelper';
import { MultiEmbedItem } from './components/embed/multi/embed-item';
import '../helpers/embedBrowserAutofiller';
import { setupHooks } from '@checkout-b/helpers/configClientHooks';
import { initConfigClient } from '@showclix-shared-util/configClient';
import { openListingImmediately, setDynamicListing } from './actions/dynamic-listing';
import { EmbeddedCalendarComponent } from "@checkout-v/series/components/EmbeddedCalendarComponent";
import { fetchReservation, setMembershipAutofill } from "@checkout/actions/reservation";
import { login } from "@checkout/actions/auth";

// eslint-disable-next-line @typescript-eslint/no-var-requires
const CHECKOUT_WRAPPER_NAME = require('../../config/checkoutWrapperName');
const EMBEDDED_COMPONENT_BODY_CLASS = 'embed-component';
const EMBEDDED_OVERLAY_BODY_CLASS = 'embed-overlay';
const EMBEDDED_CART_BODY_CLASS = 'embed-cart';

interface OpenToListingArgs {
    eventId?: string,
    slug?: string,
}

class EmbeddedCheckout {
    embedType: number;
    apiUrl: string;
    checkoutJsUrl: string;
    iconCssUrl: string;
    checkoutCssUrl: string;
    listingCssUrl: string;
    listingSlug: string;
    listingInfo;
    listingConfig: object;
    eventId: number;
    listingId: number;
    orgListingId: number;
    forCMS: boolean;
    initialized: boolean;
    mounted: boolean;
    componentFrameId: string;
    checkoutFrameId: string;
    embedToken: string;
    searchParams: object;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    appStore: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    appRouter: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    appPersistor: any;
    languageParams: LanguageParams;
    brandName: string;
    cmsMobileView: boolean;
    marketingHelper?: MarketingHelper;
    alwaysShowHiddenLevels: boolean;
    memberDetails: any;
    dynamicListing: boolean;
    styleOverrides: any;
    openToListingArgs?: OpenToListingArgs;
    orgListingConfig: any;
    renderingErrorCallback?: () => any;
    onCheckoutFrameReady?: (iframe: HTMLIFrameElement) => void;
    onLogout?: () => void;

    constructor(
        embedType,
        apiUrl,
        checkoutJsUrl,
        iconCssUrl,
        checkoutCssUrl,
        listingCssUrl,
        listingSlug,
        listingConfig,
        eventId,
        listingId,
        embedToken,
        languageParams,
        brandName,
        forCMS = false,
        listingInfo,
        searchParams = {},
        alwaysShowHiddenLevels = false,
        memberDetails = {},
        dynamicListing = false,
        styleOverrides = {},
        openToListingArgs = null,
        orgListingId = null,
        enableMarketingHelper = false
    ) {
        this.embedType        = embedType;
        this.apiUrl           = apiUrl;
        this.checkoutJsUrl    = checkoutJsUrl;
        this.iconCssUrl       = iconCssUrl;
        this.checkoutCssUrl   = checkoutCssUrl;
        this.listingCssUrl    = listingCssUrl;
        this.listingSlug      = listingSlug;
        this.listingConfig    = listingConfig;
        this.listingInfo      = listingInfo;
        this.eventId          = eventId;
        this.listingId        = listingId;
        this.orgListingId     = orgListingId;
        this.embedToken       = embedToken;
        this.forCMS           = forCMS;
        this.searchParams     = searchParams;
        this.initialized      = false;
        this.mounted          = false;
        this.componentFrameId = '';
        this.checkoutFrameId  = '';
        this.languageParams   = languageParams;
        this.brandName        = brandName;
        this.cmsMobileView    = false;
        this.alwaysShowHiddenLevels = alwaysShowHiddenLevels;
        this.memberDetails = memberDetails;
        this.dynamicListing = dynamicListing;
        this.styleOverrides = styleOverrides;
        this.openToListingArgs = openToListingArgs;
        this.orgListingConfig = {};

        if(enableMarketingHelper) {
            this.marketingHelper  = new MarketingHelper();
        }

        // Temporary disabling due to SCX-8439
        // if (listingInfo.configClient && listingInfo.configClient.configCatSdkKey) {
        //     initConfigClient(
        //       listingInfo.configClient.configCatSdkKey,
        //       setupHooks(listingInfo.configClient.leapGA4Code),
        //       listingInfo.configClient.context,
        //       listingInfo.configClient.configCatBaseUrl,
        //     );
        // }
    }

    /**
     * Fetches the required token to allow api requests
     * @param callback
     */
    fetchEmbedToken = (callback) => {
        const tokenUrl = this.apiUrl + '/embed-token';
        fetch(tokenUrl).then(async (response) => {
            if (!response.ok) {
                console.log('Not able to setup embed token.');
                return;
            }
            const tokenResponse = await response.json();
            return tokenResponse.token ?? '';
        }).then((token) => callback(token));
    }

    /**
     * Do the embedding.
     * Create frames for checkout and the embedded component.
     * Set up communication between the frames
     */
    embed = (target, checkoutTarget = null, embedToken = '') => {
        // frame ID vars
        const uniqueString    = `${(Math.random() + 1).toString(36).substring(7)}_${new Date().getTime().toString()}`;
        this.componentFrameId = `embedded_component_frame_${uniqueString}`;
        this.checkoutFrameId  = `checkout_frame_${uniqueString}`;
        this.embedToken       = embedToken;

        let caughtRenderingError = false;

        // vars to store the result of setupCheckoutApp
        let app_store = null;
        let app_router = null;
        let app_persistor = null;

        // show the checkout frame
        const show_checkout = forPinnedCart => {
            const to_show = document.getElementById(this.checkoutFrameId);
            if (to_show && !caughtRenderingError) {
                to_show.style.display = 'inline';
                if (this.dynamicListing) {
                    to_show.style.position = 'fixed';
                }
                if (app_store && !app_store.getState().ui.useFocusTrap) {
                    app_store.dispatch(setUseFocusTrap(true));
                }
                if (!forPinnedCart) {
                    this.applyCheckoutStyleToCheckoutFrame(to_show);
                }
            }
        }

        // hide the checkout frame
        const hide_checkout = () => {
            const to_hide = document.getElementById(this.checkoutFrameId);
            if (to_hide) {
                if (app_store && app_store.getState().ui.useFocusTrap) {
                    app_store.dispatch(setUseFocusTrap(false));
                }
                to_hide.style.display = 'none';
            }
        }

        // Tracks the overlay's previous state so we can detect the false→true transition.
        // We only push a history entry when the overlay first opens, not on every action.
        let previousOverlay = false;
        // Holds the current popstate listener so it can be removed when the modal closes
        // through any path other than the back button (e.g. user completes checkout).
        let cleanupOverlayPopState: ((e: PopStateEvent) => void) | null = null;

        // callback that runs every time an action is dispatched against the store
        // watch out for feedback loops here
        const action_callback = (action, store) => {
            const currentAppState = store.getState();
            const currentOverlay  = currentAppState.ui.overlay;
            const showPinnedCart = shouldPinEmbeddedCart(currentAppState) && showContinueCheckoutBanner(currentAppState);
            if (action && action.type === 'LOGOUT' && typeof this.onLogout === 'function') {
                this.onLogout();
            }
            if (action && action.type !== 'UI_SET_USE_FOCUS_TRAP') {
                if (action.type === 'LOADED_DYNAMIC_LISTING') {
                    if (window.setCssConfig) {
                        // @ts-ignore
                        const checkoutFrame = document.getElementById(this.checkoutFrameId)?.contentWindow?.document;
                        if (checkoutFrame) {
                            window.setCssConfig(currentAppState.ui.listingConfig, checkoutFrame);

                            /**
                             * For now we are forcing hero layout for dynamic listings
                             * In the future, if we want to support more, we can grab the selected
                             * layout here from currentAppState.ui.uiConfig.layout.
                             */
                            const selectedLayout = 'hero';
                            const layoutStylesheetLink = checkoutFrame.getElementById('checkout_layout_stylesheet');
                            if (layoutStylesheetLink && currentAppState.ui?.uiConfig?.siteCss) {
                                const layoutCssUrl = currentAppState.ui.uiConfig.siteCss?.[selectedLayout];
                                if (layoutCssUrl) {
                                    layoutStylesheetLink.href = layoutCssUrl;
                                }
                            }
                        }
                    }
                } else if (currentOverlay) {
                    // When the overlay first becomes visible, push a single history entry to
                    // the parent page so the browser back button closes the modal. This is the
                    // standard "push state for modal" pattern — one pushState on open, one
                    // popstate listener that closes the overlay on back. This replaces the
                    // accidental history-push side effect that previously came from HashRouter
                    // iframe navigation; MemoryRouter no longer produces that side effect.
                    if (!previousOverlay) {
                        window.history.pushState({ checkoutOverlayOpen: true }, '');
                        const onPopState = (_e: PopStateEvent) => {
                            window.removeEventListener('popstate', onPopState);
                            cleanupOverlayPopState = null;
                            if (app_store && app_store.getState().ui.overlay) {
                                app_store.dispatch(setCheckoutOverlay(false));
                            }
                        };
                        window.addEventListener('popstate', onPopState);
                        cleanupOverlayPopState = onPopState;
                    }
                    previousOverlay = true;
                    show_checkout(false);
                } else {
                    // Overlay closed through a path other than back button (e.g. checkout
                    // completed, modal dismissed via UI). Clean up the popstate listener so
                    // a subsequent back press doesn't re-close a modal that's already closed,
                    // and navigate back in history to remove the entry we pushed on open.
                    if (previousOverlay && cleanupOverlayPopState) {
                        window.removeEventListener('popstate', cleanupOverlayPopState);
                        cleanupOverlayPopState = null;
                        window.history.back();
                    }
                    previousOverlay = false;
                    if (showPinnedCart && !caughtRenderingError) {
                        const frameToUpdate = document.getElementById(this.checkoutFrameId);
                        if (frameToUpdate) {
                            const cartPosition = getEmbedCartBehavior(currentAppState);
                            this.applyCartStyleToCheckoutFrame(frameToUpdate, cartPosition);
                            show_checkout(true);
                        }
                    } else {
                        hide_checkout();
                    }
                }
            }
        }

        // listen for the checkout frames to finish loading the checkout script
        window.addEventListener('message', e => {
            let target_frame = null;
            if (e.data === this.checkoutFrameId) {
                target_frame = this.getCheckoutFrame();
                if (target && target_frame && target_frame.setupAndMountEmbeddedCheckout) {

                    // set up the app, mount checkout
                    const setup_result = target_frame.setupAndMountEmbeddedCheckout(action_callback);
                    app_store = setup_result.store;
                    app_router = setup_result.router;
                    app_persistor = setup_result.persistor;
                    this.appStore = app_store;
                    this.appRouter = app_router;
                    this.appPersistor = app_persistor;

                    // add the frame for the embedded component
                    const embedFrame = this.setupCheckoutFrame(target, this.componentFrameId, EMBEDDED_COMPONENT_BODY_CLASS);
                    if (this.embedType == EMBED_TYPE_BUTTON) {
                        embedFrame.style.height = '1px';
                        embedFrame.style.maxHeight = '100%';
                        embedFrame.style.width = '100%';
                        embedFrame.style.maxWidth = '100%';
                        IframeResizer.iframeResize({
                            checkOrigin: [window.location.origin],
                        }, embedFrame);
                    } else {
                        // calendar, calendar w/ inventory filter, ticketpicker, seat picker
                        embedFrame.style.width = '100%' // responsive
                        // this is responsive to the window width, not the container size
                        IframeResizer.iframeResize({
                            checkOrigin: [window.location.origin],
                            heightCalculationMethod: 'bodyScroll'
                        }, embedFrame);
                    }
                }
            }
            if (e.data === this.componentFrameId) {
                target_frame = this.getComponentFrame();
                if (target_frame && target_frame.renderEmbeddedComponent) {
                    target_frame.renderEmbeddedComponent(app_store, app_persistor);
                    this.mounted = true;
                    const target_frame_element = document.getElementById(this.componentFrameId);
                    if (target_frame_element) {
                        target_frame_element.style.display = 'inline';
                    }
                }
            }
            if (e.data === 'ERROR_CAUGHT_' + this.checkoutFrameId && !caughtRenderingError) {
                caughtRenderingError = true;
                hide_checkout();
                this?.renderingErrorCallback();
            }
        });

        // allow specifying where to put checkout for the CMS
        const frameTarget = this.forCMS && !!checkoutTarget ? checkoutTarget : document.body;

        // add the checkout frame.  this kicks off the process of setting up checkout and mounting the embedded component
        this.setupCheckoutFrame(frameTarget, this.checkoutFrameId, EMBEDDED_OVERLAY_BODY_CLASS);
        const checkout_frame_element = document.getElementById(this.checkoutFrameId);
        if (checkout_frame_element && !caughtRenderingError) {
            this.applyCheckoutStyleToCheckoutFrame(checkout_frame_element);
        }
    }

    /**
     * Apply styles to checkout frame for rendering full screen checkout
     */
    applyCheckoutStyleToCheckoutFrame = frame => {
        frame.setAttribute('frameBorder', '0');
        frame.style.position = this.forCMS ? 'absolute' : 'fixed';
        frame.style.top = '0';
        frame.style.right = '';
        frame.style.left = this.cmsMobileView ? '50%' : '0';
        frame.style.height = '100%';
        frame.style.width = this.cmsMobileView ? '320px' : '100%';
        frame.style.zIndex = '999999';
        frame.style.removeProperty('max-width');
        if (this.cmsMobileView) {
            frame.style.transform = 'translateX(-50%)';
        } else {
            frame.style.removeProperty('transform');
        }
        if (frame?.contentWindow?.document?.body) {
            frame.contentWindow.document.body.classList.remove(EMBEDDED_CART_BODY_CLASS);
            frame.contentWindow.document.body.classList.add(EMBEDDED_OVERLAY_BODY_CLASS);
            frame.classList.add(EMBEDDED_CART_BODY_CLASS);
            frame.classList.add(EMBEDDED_OVERLAY_BODY_CLASS);
        }
    }

    /**
     * Apply styles to checkout frame for rendering pinned cart
     */
    applyCartStyleToCheckoutFrame = (frame, position = EMBED_CART_BEHAVIOR_PIN_TO_TOP) => {
        frame.style.position = 'absolute';
        frame.style.height = '60px';
        frame.style.maxWidth = this.cmsMobileView ? '320px' : '562px';
        frame.style.left = '50%'
        frame.style.transform = 'translateX(-50%)';
        if (position == EMBED_CART_BEHAVIOR_PIN_TO_BOTTOM) {
            frame.style.removeProperty('top');
            frame.style.bottom = '0';
        } else {
            frame.style.removeProperty('bottom');
            frame.style.top = '0';

        }
        if (frame?.contentWindow?.document?.body) {
            frame.contentWindow.document.body.classList.remove(EMBEDDED_OVERLAY_BODY_CLASS);
            frame.contentWindow.document.body.classList.add(EMBEDDED_CART_BODY_CLASS);
            frame.classList.remove(EMBEDDED_OVERLAY_BODY_CLASS);
            frame.classList.add(EMBEDDED_CART_BODY_CLASS);
        }

        const allowedOverrides = [
            'transform',
            'position',
            'height',
            'maxWidth',
            'left',
            'right',
            'top',
            'bottom',
        ];
        if (typeof this.styleOverrides?.cart === 'object' && this.styleOverrides.cart !== null) {
            Object.keys(this.styleOverrides.cart).forEach(styleKey => {
                if (allowedOverrides.includes(styleKey)) {
                    frame.style[styleKey] = this.styleOverrides.cart[styleKey];
                }
            });
        }
    }

    /**
     * Refetch reservation using token and secret
     * @param token
     * @param secret
     */
    refetchReservationAndLogin = (token, secret, member, membershipAutofill = null) => {
        if (membershipAutofill) {
            this.appStore.dispatch(setMembershipAutofill(membershipAutofill));
        }
        this.appStore.dispatch(fetchReservation(token, secret));
        this.appStore.dispatch(login(member.email,member.first_name,member.last_name));
    }

    getStore = () => {
        return this.appStore;
    }

    /**
     * Set up the store, router, and persistor
     */
    setupApp = (brandName, actionCallback) => {
        let persistSuffix = `:patrontech:embed`;
        if (this.listingId) {
            persistSuffix += `:${this.listingId}`;
        } else if (this.orgListingId) {
            persistSuffix += `:orgListing:${this.orgListingId}`;
        }
        const result = setupApp(brandName, actionCallback, persistSuffix);

        result.store.dispatch(setDynamicListing(!!this.dynamicListing));

        // this is an embedded checkout, flag it here
        result.store.dispatch(setIsEmbed(true));

        // set the embed type
        result.store.dispatch(setEmbedType(this.embedType));

        result.store.dispatch(setAlwaysShowHiddenLevels(this.alwaysShowHiddenLevels));
        // set org listing config if provided
        result.store.dispatch(setOrgListingConfig(this.orgListingConfig || {}));

        // checkout will be hidden when this mounts, avoid many focus trap errors
        result.store.dispatch(setUseFocusTrap(false));

        return result;
    }

    /**
     * Render configured embedded component
     */
    renderEmbeddedComponent = (element, store, persistor, listingCssUrl = null, alwaysShowHiddenLevels= null) => {
        let component = null;

        // update embed type in store when changing in CMS
        if (this.embedType) {
            store.dispatch(setEmbedType(this.embedType));
        }
        store.dispatch(setAlwaysShowHiddenLevels(alwaysShowHiddenLevels));

        setEmbedToken(this.embedToken);

        // don't render any embedded component
        if (this.embedType == EMBED_TYPE_NO_COMPONENT) {
            return;
        }

        const root = createRoot(element);
        root.render(
            <Provider store={store}>
                {/*@ts-ignore*/}
                <PersistGate loading={null} persistor={persistor}>
                    {!!listingCssUrl && <link rel="stylesheet" href={listingCssUrl} />}
                    <EmbeddedComponentWrapper apiUrl={this.apiUrl} translations={this.languageParams?.translations ?? {}}>
                      {this.embedType == EMBED_TYPE_CALENDAR && (<EmbeddedCalendarComponent />)}
                      {this.embedType == EMBED_TYPE_TICKET_LIST && (
                        <div id="checkout-overlay"><EmbeddedTicketPicker /></div>
                      )}
                      {this.embedType == EMBED_TYPE_MULTI && (
                        <MultiEmbedItem listingConfig={this.listingConfig} />
                      )}
                      {!([EMBED_TYPE_CALENDAR, EMBED_TYPE_TICKET_LIST, EMBED_TYPE_MULTI].includes(this.embedType)) && (
                        <div className="embed__button"><TicketsButtonContainer /></div>
                      )}
                    </EmbeddedComponentWrapper>
                </PersistGate>
            </Provider>
        )
    }

    /**
     * Render the checkout app to the provided element
     */
    renderCheckout = (element, router, store, persistor, frameId: string | null = null) => {

        setEmbedToken(this.embedToken);

        let hasPostedErrorCaughtMessage = false;

        const root = createRoot(element);
        root.render(
            <Provider store={store}>
                {/*@ts-ignore*/}
                <PersistGate loading={null} persistor={persistor}>
                    <App
                        router={router}
                        store={store}
                        apiUrl={this.apiUrl}
                        listingSlug={this.listingSlug}
                        listingConfig={this.listingConfig}
                        listingInfo={this.listingInfo}
                        eventId={this.eventId}
                        listingId={this.listingId}
                        language={this.languageParams}
                        brandName={this.brandName}
                        searchParams={this.searchParams}
                        alwaysShowHiddenLevels={this.alwaysShowHiddenLevels}
                        memberDetails={this.memberDetails}
                        dynamicListing={this.dynamicListing}
                        openToListingArgs={this.openToListingArgs}
                        stopRenderingOnError={true}
                        errorHandler={() => {
                            if (!hasPostedErrorCaughtMessage) {
                                hasPostedErrorCaughtMessage = true;
                                window.top.postMessage('ERROR_CAUGHT_' + frameId);
                            }
                        }}
                    />
                </PersistGate>
            </Provider>
        )
    }

    getCheckoutFrame = () => {
        return window.frames[this.checkoutFrameId];
    }

    getComponentFrame = () => {
        return window.frames[this.componentFrameId];
    }

    /**
     * Append a hidden iframe with checkout js and styles written to it
     * @param target   - the element to append the iframe to
     * @param frame_id - set the iframe id and name attributes to this
     */
    private setupCheckoutFrame = (target, frame_id: string, body_class = null) => {

        const bodyClasses = body_class ? [body_class] : [];
        if (this.forCMS) {
            bodyClasses.push('embed-cms');
        }

        if (this.embedType == EMBED_TYPE_MULTI) {
            bodyClasses.push('embed-listing-item');
        }

        const bodyClassString = bodyClasses.length ? `class="${bodyClasses.join(' ')}"` : '';

        const this_frame = document.createElement('iframe');
        this_frame.id = frame_id;
        this_frame.name = frame_id;
        this_frame.src = 'about:blank';
        this_frame.allow = 'payment';

        if (frame_id.startsWith('checkout_frame_') && typeof this.onCheckoutFrameReady === 'function') {
          this.onCheckoutFrameReady(this_frame);
        }

        if (this.embedType == EMBED_TYPE_MULTI && frame_id.startsWith('embedded_component_frame_')) {
            this_frame.className = 'embed_frame';
        }

        this_frame.frameBorder = '0';
        this_frame.style.display = 'none';
        const appended_frame = target.appendChild(this_frame);
        const this_frame_document = appended_frame.contentWindow.document;
        this_frame_document.open();
        this_frame_document.write(`<link rel="stylesheet" href="${this.iconCssUrl}">`);
        this_frame_document.write(`<link rel="stylesheet" href="${this.checkoutCssUrl}">`);
        this_frame_document.write(`<link id="checkout_layout_stylesheet" rel="stylesheet" href="">`);
        if (!this.forCMS) {
            this_frame_document.write(`<link rel="stylesheet" href="${this.listingCssUrl}">`);
        }
        this_frame_document.write(`<body ${bodyClassString}><div id="root"></div></body>`);
        this_frame_document.write(this.getFrameJavascript(frame_id));
        this_frame_document.close();

        if (this.embedType == EMBED_TYPE_MULTI && frame_id.startsWith('checkout_frame_')) {
            const analytics_container = document.createElement('iframe');

            analytics_container.id = "analytics-container";
            analytics_container.style.visibility = "hidden";
            analytics_container.style.width = "0";
            analytics_container.style.height = "0";
            analytics_container.style.display = "none";
            analytics_container.src=`/listing/${this.listingSlug}/analytics`;
            // @ts-ignore
            analytics_container.sandbox = "allow-scripts allow-same-origin";
            this_frame_document.body.appendChild(analytics_container);
        }

        return appended_frame;
    }

    /**
     * Each frame will load the checkout source and post a message to the parent once loaded
     * Additionally, expose functions that can be called from the parent window to mount
     * checkout components within the frame
     */
    private getFrameJavascript(frameId: string) {

        // enable modifying checkout config for CMS live preview
        const setFieldFunc = !this.forCMS ? '' : `
            function setCheckoutArg(field, value) {
                checkoutArgs[field] = value;
            }
        `;

        return `
            <script>

                var checkoutArgs = {
                    embedType: ${this.embedType},
                    apiUrl: '${this.apiUrl}',
                    checkoutJsUrl: '${this.checkoutJsUrl}',
                    iconCssUrl: '${this.iconCssUrl}',
                    checkoutCssUrl: '${this.checkoutCssUrl}',
                    listingCssUrl: '${this.listingCssUrl}',
                    listingSlug: '${this.listingSlug}',
                    listingInfo: ${JSON.stringify(this.listingInfo)},
                    listingConfig: ${JSON.stringify(this.listingConfig)},
                    eventId: ${this.eventId},
                    listingId: ${this.listingId},
                    orgListingId: ${this.orgListingId},
                    languageParams: ${JSON.stringify(this.languageParams)},
                    embedToken: '${this.embedToken}',
                    brandName: '${this.brandName}',
                    searchParams: ${JSON.stringify(this.searchParams)},
                    alwaysShowHiddenLevels: ${this.alwaysShowHiddenLevels},
                    memberDetails: ${JSON.stringify(this.memberDetails)},
                    dynamicListing: ${this.dynamicListing ? 'true' : 'false'},
                    openToListingArgs: ${this.openToListingArgs ? JSON.stringify(this.openToListingArgs) : 'null'},
                    orgListingConfig: ${JSON.stringify(this.orgListingConfig)},
                };

                ${setFieldFunc}

                function createEmbeddedCheckout() {
                    const checkoutApp = new ${CHECKOUT_WRAPPER_NAME}.default(
                        checkoutArgs.embedType,
                        checkoutArgs.apiUrl,
                        checkoutArgs.checkoutJsUrl,
                        checkoutArgs.iconCssUrl,
                        checkoutArgs.checkoutCssUrl,
                        checkoutArgs.listingCssUrl,
                        checkoutArgs.listingSlug,
                        checkoutArgs.listingConfig,
                        checkoutArgs.eventId,
                        checkoutArgs.listingId,
                        checkoutArgs.embedToken,
                        checkoutArgs.languageParams,
                        checkoutArgs.brandName,
                        false, // forCMS
                        checkoutArgs.listingInfo,
                        checkoutArgs.searchParams,
                        checkoutArgs.alwaysShowHiddenLevels,
                        checkoutArgs.memberDetails,
                        checkoutArgs.dynamicListing,
                        {}, // style overrides, not necessary here
                        checkoutArgs.openToListingArgs,
                        checkoutArgs.orgListingId
                    );
                    checkoutApp.setOrgListingConfig(checkoutArgs.orgListingConfig);
                    return checkoutApp;
                }

                function renderCheckout(router, store, persistor) {
                    createEmbeddedCheckout().renderCheckout(document.getElementById('root'), router, store, persistor, '` + frameId + `');
                }

                function setupAndMountEmbeddedCheckout(actionCallback) {
                    var setupResult = createEmbeddedCheckout().setupApp(checkoutArgs.brandName, actionCallback);
                    renderCheckout(setupResult.router, setupResult.store, setupResult.persistor);
                    return setupResult;
                }

                function renderEmbeddedComponent(store, persistor) {
                    createEmbeddedCheckout().renderEmbeddedComponent(
                        document.getElementById('root'),
                        store,
                        persistor,
                        checkoutArgs.listingConfig.skipListingCss ? null : checkoutArgs.listingCssUrl,
                        checkoutArgs.alwaysShowHiddenLevels ?  checkoutArgs.alwaysShowHiddenLevels : null,
                    );
                }

                let script = document.createElement('script');
                let loaded = false;
                script.src = checkoutArgs.checkoutJsUrl;
                script.addEventListener('load', () => {
                    loaded = true;
                    window.top.postMessage('` + frameId + `', '*');
                });
                script.addEventListener('error', (e) => {
                    reject(e);
                });
                document.body.appendChild(script);

                /* Modernizr needed for touch and hover event styles */

                /*! modernizr 3.6.0 (Custom Build) | MIT *
                * https://modernizr.com/download/?-touchevents-setclasses !*/
                !function(e,n,t){function o(e,n){return typeof e===n}function s(){var e,n,t,s,a,i,r;for(var l in c)if(c.hasOwnProperty(l)){if(e=[],n=c[l],n.name&&(e.push(n.name.toLowerCase()),n.options&&n.options.aliases&&n.options.aliases.length))for(t=0;t<n.options.aliases.length;t++)e.push(n.options.aliases[t].toLowerCase());for(s=o(n.fn,"function")?n.fn():n.fn,a=0;a<e.length;a++)i=e[a],r=i.split("."),1===r.length?Modernizr[r[0]]=s:(!Modernizr[r[0]]||Modernizr[r[0]]instanceof Boolean||(Modernizr[r[0]]=new Boolean(Modernizr[r[0]])),Modernizr[r[0]][r[1]]=s),f.push((s?"":"no-")+r.join("-"))}}function a(e){var n=u.className,t=Modernizr._config.classPrefix||"";if(p&&(n=n.baseVal),Modernizr._config.enableJSClass){var o=new RegExp("(^|\\s)"+t+"no-js(\\s|$)");n=n.replace(o,"$1"+t+"js$2")}Modernizr._config.enableClasses&&(n+=" "+t+e.join(" "+t),p?u.className.baseVal=n:u.className=n)}function i(){return"function"!=typeof n.createElement?n.createElement(arguments[0]):p?n.createElementNS.call(n,"http://www.w3.org/2000/svg",arguments[0]):n.createElement.apply(n,arguments)}function r(){var e=n.body;return e||(e=i(p?"svg":"body"),e.fake=!0),e}function l(e,t,o,s){var a,l,f,c,d="modernizr",p=i("div"),h=r();if(parseInt(o,10))for(;o--;)f=i("div"),f.id=s?s[o]:d+(o+1),p.appendChild(f);return a=i("style"),a.type="text/css",a.id="s"+d,(h.fake?h:p).appendChild(a),h.appendChild(p),a.styleSheet?a.styleSheet.cssText=e:a.appendChild(n.createTextNode(e)),p.id=d,h.fake&&(h.style.background="",h.style.overflow="hidden",c=u.style.overflow,u.style.overflow="hidden",u.appendChild(h)),l=t(p,e),h.fake?(h.parentNode.removeChild(h),u.style.overflow=c,u.offsetHeight):p.parentNode.removeChild(p),!!l}var f=[],c=[],d={_version:"3.6.0",_config:{classPrefix:"",enableClasses:!0,enableJSClass:!0,usePrefixes:!0},_q:[],on:function(e,n){var t=this;setTimeout(function(){n(t[e])},0)},addTest:function(e,n,t){c.push({name:e,fn:n,options:t})},addAsyncTest:function(e){c.push({name:null,fn:e})}},Modernizr=function(){};Modernizr.prototype=d,Modernizr=new Modernizr;var u=n.documentElement,p="svg"===u.nodeName.toLowerCase(),h=d._config.usePrefixes?" -webkit- -moz- -o- -ms- ".split(" "):["",""];d._prefixes=h;var m=d.testStyles=l;Modernizr.addTest("touchevents",function(){var t;if("ontouchstart"in e||e.DocumentTouch&&n instanceof DocumentTouch)t=!0;else{var o=["@media (",h.join("touch-enabled),("),"heartz",")","{#modernizr{top:9px;position:absolute}}"].join("");m(o,function(e){t=9===e.offsetTop})}return t}),s(),a(f),delete d.addTest,delete d.addAsyncTest;for(var v=0;v<Modernizr._q.length;v++)Modernizr._q[v]();e.Modernizr=Modernizr}(window,document);
            </script>
        `;
    }

    openCheckoutToListing = async (listingSlug: string, eventId: string) => {
        let result = false;
        if (this.appStore) {
            result = await this.appStore.dispatch(openListingImmediately(listingSlug, eventId));
        }
        return result;
    }

    updateOrgListingConfig = (orgListingConfig: object) => {
        if (this.appStore) {
            this.appStore.dispatch(updateOrgListingConfig(orgListingConfig));
        }
    }

    setRenderingErrorCallback(callback: () => any) {
        this.renderingErrorCallback = callback;
    }

    setOnCheckoutFrameReady(callback: (iframe: HTMLIFrameElement) => void) {
        this.onCheckoutFrameReady = callback;
    }

    setOnLogout(callback: () => void) {
        this.onLogout = callback;
    }

    setOrgListingConfig(config: any) {
        this.orgListingConfig = config || {};
        // If the app store is already initialized, dispatch the action to update Redux state
        if (this.appStore) {
            this.appStore.dispatch(setOrgListingConfig(this.orgListingConfig));
        }
    }
}

interface LanguageParams {
    translations: object,
}

export default EmbeddedCheckout;
